import type { Node } from '../../../core/models/scene';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';

/**
 * Per-diagram snapshot of the *resolved* org chart view.
 *
 * Deciding the initial overview (auto-collapse) and the initial framing
 * (zoom-to-fit) is first-open work, not per-load work: the answer is
 * deterministic and has not changed since the last visit. Recomputing it on
 * every refresh means hydrating the full tree, laying it out, collapsing it,
 * laying it out again and only then fitting — all of it visible, and all of it
 * throwing away the viewport the user had.
 *
 * This module persists the answer so a refresh only has to restore it. It
 * lives in localStorage deliberately: it must be readable *synchronously at
 * mount*, long before Yjs reports synced, so the collapsed set is already in
 * place the first time the projection sees nodes.
 */

export const ORG_VIEW_SNAPSHOT_VERSION = 1;

export interface OrgViewStage {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface OrgViewSnapshot {
  v: number;
  stage: OrgViewStage;
  /** Signature of the rendered picture the stage was captured against. */
  contentSig: string;
}

const snapshotKey = (diagramId: string): string => `creately-orgview-${diagramId}`;
/** Pre-existing key owned by App's collapse persistence — read-only here. */
const collapseKey = (diagramId: string): string => `creately-collapse-${diagramId}`;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * FNV-1a. Keeps the signature short enough to store and cheap enough to
 * recompute on every projection change for a few hundred nodes.
 */
const hashString = (input: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

/**
 * Signature of the picture currently on the canvas: which nodes are visible
 * and where they sit. A saved stage is only reusable when this matches — the
 * offsets are meaningless against a different layout.
 *
 * Positions are rounded so sub-pixel layout jitter does not invalidate a
 * perfectly good viewport.
 */
export const computeOrgContentSignature = (nodes: readonly Node[]): string => {
  if (nodes.length === 0) return '';
  const parts = nodes
    .map((node) => {
      const w = Math.round(resolveEffectiveShapeDimension(node, 'width') ?? 270);
      const h = Math.round(resolveEffectiveShapeDimension(node, 'height') ?? 80);
      return `${node.id}:${Math.round(node.x)}:${Math.round(node.y)}:${w}:${h}`;
    })
    .sort();
  return `${nodes.length}-${hashString(parts.join('|'))}`;
};

export const readOrgViewSnapshot = (diagramId: string | null | undefined): OrgViewSnapshot | null => {
  if (!diagramId) return null;
  try {
    const raw = localStorage.getItem(snapshotKey(diagramId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OrgViewSnapshot> | null;
    if (!parsed || parsed.v !== ORG_VIEW_SNAPSHOT_VERSION) return null;
    const stage = parsed.stage;
    if (
      !stage ||
      !isFiniteNumber(stage.scale) ||
      !isFiniteNumber(stage.offsetX) ||
      !isFiniteNumber(stage.offsetY) ||
      stage.scale <= 0
    ) {
      return null;
    }
    if (typeof parsed.contentSig !== 'string' || parsed.contentSig.length === 0) return null;
    return { v: ORG_VIEW_SNAPSHOT_VERSION, stage, contentSig: parsed.contentSig };
  } catch {
    return null;
  }
};

export const writeOrgViewSnapshot = (
  diagramId: string | null | undefined,
  stage: OrgViewStage,
  contentSig: string,
): void => {
  if (!diagramId || !contentSig) return;
  if (!isFiniteNumber(stage.scale) || stage.scale <= 0) return;
  if (!isFiniteNumber(stage.offsetX) || !isFiniteNumber(stage.offsetY)) return;
  const snapshot: OrgViewSnapshot = {
    v: ORG_VIEW_SNAPSHOT_VERSION,
    stage: {
      scale: Math.round(stage.scale * 1e4) / 1e4,
      offsetX: Math.round(stage.offsetX),
      offsetY: Math.round(stage.offsetY),
    },
    contentSig,
  };
  try {
    localStorage.setItem(snapshotKey(diagramId), JSON.stringify(snapshot));
  } catch {
    /* quota or private browsing — the view simply re-fits next time */
  }
};

/**
 * The saved viewport, but only when it is still meaningful: the offsets were
 * computed against a specific layout, so a chart that has changed shape since
 * must be re-fitted rather than restored into a stale frame.
 */
export const resolveRestorableOrgStage = (
  diagramId: string | null | undefined,
  contentSignature: string,
): OrgViewStage | null => {
  if (!diagramId || !contentSignature) return null;
  const snapshot = readOrgViewSnapshot(diagramId);
  if (!snapshot || snapshot.contentSig !== contentSignature) return null;
  return snapshot.stage;
};

export const clearOrgViewSnapshot = (diagramId: string | null | undefined): void => {
  if (!diagramId) return;
  try {
    localStorage.removeItem(snapshotKey(diagramId));
  } catch {
    /* ignore */
  }
};

/**
 * Collapse state persisted by App (Y.Doc + localStorage). Read here so the
 * canvas can seed the store at mount instead of waiting for the post-sync
 * restore, which loses the race against node hydration.
 */
export const readPersistedCollapsedNodeIds = (diagramId: string | null | undefined): string[] | null => {
  if (!diagramId) return null;
  try {
    const raw = localStorage.getItem(collapseKey(diagramId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const ids = parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
};

/**
 * Signature of what the canvas is currently rendering. Published by the org
 * canvas so the (canvas-agnostic) stage persister can tag a saved viewport
 * without threading rendered nodes through the app shell.
 */
let activeContentSignature = '';

export const setActiveOrgContentSignature = (signature: string): void => {
  activeContentSignature = signature;
};

export const getActiveOrgContentSignature = (): string => activeContentSignature;
